/**
 * 로그인 뒤 돌아갈 콘솔 안의 경로 (#142, #226)
 *
 * 서비스가 콘솔 로그인으로 사용자를 보내면 `/login?redirect=/oidc/authorize?…` 로 온다.
 * 로그인 방법(비밀번호 · 구글 · 쿠키)과 상관없이 로그인이 끝나면 **그 경로가 가장 먼저**다.
 * 구글은 콘솔 밖을 다녀오므로 주소의 `redirect` 가 사라진다 — 같은 탭의 sessionStorage 에 맡겨 둔다.
 *
 * 맡긴 경로는 10분만 쓴다. 서비스 로그인을 하다 그만둔 탭에서 나중에 콘솔에 로그인했는데
 * 그 서비스로 튕겨 나가면 안 된다.
 */
const STORAGE_KEY = 'login_redirect';
const MAX_AGE_MS = 10 * 60 * 1000;

/**
 * **`/` 로 시작하는 콘솔 안의 경로만** 받는다.
 *
 * 그대로 믿으면 열린 리다이렉트가 된다 — `?redirect=https://evil.com` 으로 사용자를
 * 남의 사이트에 떨어뜨리면서 우리 로그인 화면을 거치게 만들 수 있다.
 * `//evil.com` 은 브라우저가 프로토콜 상대 주소로 읽으므로 이것도 막는다.
 */
export const safeRedirectPath = (value: string | null): string | null => {
  if (!value) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value;
};

/** 로그인 화면에 들어올 때 `?redirect=` 가 있으면 맡겨 둔다 */
export const rememberLoginRedirect = (search: string): void => {
  const path = safeRedirectPath(new URLSearchParams(search).get('redirect'));
  if (!path) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch (e) {
    console.warn('[login] sessionStorage 를 쓸 수 없어 로그인 뒤 돌아갈 경로를 맡기지 못했습니다', e);
  }
};

/** 로그인이 끝났을 때 돌아갈 경로. 한 번 꺼내면 지운다 */
export const takeLoginRedirect = (): string | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw) as { path?: string; at?: number };
    if (typeof at !== 'number' || Date.now() - at > MAX_AGE_MS) return null;
    return safeRedirectPath(path ?? null);
  } catch (e) {
    console.warn('[login] 로그인 뒤 돌아갈 경로를 읽지 못했습니다', e);
    return null;
  }
};
